/**
 * Inline labor-rate editor (Spec 016).
 *
 * Renders as an expanded form below the labor row. Saves via
 * `patchLabor` with `If-Match` optimistic concurrency.
 */

import { useState } from "react";

import { patchLabor } from "@starz/shared/api/sdk.gen";
import type { LaborRate, MaterialUnitKind } from "@starz/shared/api/types.gen";

const UNITS: MaterialUnitKind[] = ["sqft", "linear_ft", "each", "gallon"];

interface Props {
  rate: LaborRate;
  onSaved: (rate: LaborRate) => void;
  onCancel: () => void;
}

export function LaborRowEditor({ rate, onSaved, onCancel }: Props) {
  const [displayName, setDisplayName] = useState(rate.display_name);
  const [unit, setUnit] = useState<MaterialUnitKind>(rate.unit);
  const [priceDollars, setPriceDollars] = useState(
    (rate.unit_price_cents / 100).toFixed(2),
  );
  const [notes, setNotes] = useState(rate.notes ?? "");
  const [active, setActive] = useState(rate.active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSave() {
    setError(null);
    const cents = Math.round(parseFloat(priceDollars) * 100);
    if (Number.isNaN(cents) || cents < 0) {
      setError("Price must be a non-negative number.");
      return;
    }
    setSaving(true);
    const result = await patchLabor({
      taskId: rate.task_id,
      ifMatchVersion: rate.version,
      body: {
        display_name: displayName.trim(),
        unit,
        unit_price_cents: cents,
        notes: notes.trim() || null,
        active,
      },
    });
    setSaving(false);
    if ("error" in result) {
      setError(
        result.error.error.code === "CONFLICT"
          ? "This labor rate changed elsewhere — reload and retry."
          : result.error.error.message,
      );
      return;
    }
    onSaved(result.data);
  }

  const inputClass =
    "mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm";

  return (
    <div className="space-y-3 rounded-lg border border-starz-gold/20 bg-starz-cream/40 p-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
            Display name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
            Unit
          </label>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value as MaterialUnitKind)}
            className={inputClass}
          >
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
            Price ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={priceDollars}
            onChange={(e) => setPriceDollars(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
          Notes
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={inputClass}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-starz-charcoal">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4"
        />
        Active
      </label>
      {error ? (
        <p className="border-l-2 border-starz-brick pl-3 text-xs text-starz-brick">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border border-starz-gold/30 px-3 py-1.5 text-xs font-medium text-starz-charcoal hover:bg-white"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={saving}
          className="rounded-md bg-starz-navy px-4 py-1.5 text-xs font-semibold text-starz-off-white hover:opacity-90 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
